import React, { createElement, forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { focusFirstInvalid } from 'src/common/style/a11y.style';
import { colors } from 'src/theme';
import { Button } from '../buttons';
import { Flex } from '../grid';
import { ArrowRightIcon } from '../icons';
import { Validation, validationRules } from './validation';

// Definiraj tipove za konfiguraciju polja
export interface FieldConfig<T> {
  name: string;
  component: React.FC<any>;
  componentProps?: { [key: string]: any };
  label?: string;
  validations?: Validation[];
  options?: { label: string; value: string }[];
  disabled?: boolean;
  disabledIf?: (values: any) => boolean;
  readOnly?: boolean;
  error?: string;
  showIf?: (values: T) => boolean;
  formatValue?: (values: any) => string;
  onChange?: (value: any) => void;  // , formValues: { [key: string]: any } moze se dodati kao parametar ako će trebati vrijednosti cijele forme
  onBlur?: (value: any) => void;
  validateOnBlur?: boolean;
  beforeElement?: React.ReactNode;
  afterElement?: React.ReactNode;
}

export interface FormConfig {
  gap?: string | number;
  button?: string;
}

// export const getNestedValue = (obj: any, path: string): any => {
//   return path.split('.').reduce((acc, part) => acc && acc[part], obj);
// };

// const setNestedValue = (obj: any, path: string, value: any) => {
//   const keys = path.split('.');
//   let current = obj;
//   for (let i = 0; i < keys.length - 1; i++) {
//     if (!current[keys[i]]) {
//       current[keys[i]] = {};
//     }
//     current = current[keys[i]];
//   }
//   current[keys[keys.length - 1]] = value;
// };

export interface FormRef {
  getValues?: () => { [key: string]: any };
  submitForm: () => void;
  validateForm?: () => boolean;
  setFieldValue?: (fieldName: string, value: any, validation?: boolean) => void;
}

export interface FormProps<T> {
  fields: FieldConfig<T>[];
  config?: FormConfig;
  initialValues?: Partial<T>;
  onSubmit: (values: T) => void;
  reduxName?: string;
}

export const Form = forwardRef<FormRef, FormProps<any>>(({ fields, initialValues, config, onSubmit }: FormProps<any>, ref) => {
  const [formValues, setFormValues] = useState<{ [key: string]: any }>(initialValues || {});
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const formElementRef = useRef<HTMLFormElement>(null);


  const configDefault: FormConfig = {
    gap: 24,
    ...config
  };

  const validateField = (fieldName: string, value: any): string | null => {
    const fieldConfig = fields.find((field) => field.name === fieldName);
    if (fieldConfig && fieldConfig.validations) {
      for (const validation of fieldConfig.validations) {
        const params = validation.params ? Object.keys(validation.params).reduce((acc, key) => {
          acc[key] = formValues[validation.params![key]] !== undefined ? formValues[validation.params![key]] : validation.params![key];
          return acc;
        }, {} as { [key: string]: any }) : undefined;

        if (!validationRules[validation.type](value, params)) {
          return validation.message;
        }
      }
    }
    return null;
  };

  const handleFieldChange = (fieldName: string, value: any, onChange?: (value: any) => void) => {
    // if (value instanceof File) {
    //   // Ako je vrijednost File objekt, spremi ga u fileValues
    //   setFileValues(prev => ({
    //     ...prev,
    //     [fieldName]: value
    //   }));
    // } else {
      setFormValues((prevValues) => {
        if (value instanceof File) {
          return {
            ...prevValues,
            [fieldName]: value
          };
        }
        if (typeof value === 'object' && !Array.isArray(value)) {
          // Ako je value objekt, ažuriraj sve vrijednosti unutar objekta
          return {
            ...prevValues,
            ...value,
          };
        }
        // Inače, ažuriraj samo jednu vrijednost
        return {
          ...prevValues,
          [fieldName]: value,
        };
      });
    // }

    if (typeof value === 'object' && !Array.isArray(value)) {
      Object.keys(value).forEach((key) => {
        if (!(key === 'street' && value[key] === '')) {
          const errorMessage = validateField(key, value[key]);
          setErrors((prevErrors) => ({
            ...prevErrors,
            [key]: errorMessage || '',
          }));
        }
      });
    } else {
      const errorMessage = validateField(fieldName, value);
      setErrors((prevErrors) => ({
        ...prevErrors,
        [fieldName]: errorMessage || '',
      }));
    }

    // Pozovi onChange funkciju ako postoji
    if (onChange) onChange(value);
  };

  const handleFieldBlur = (field: FieldConfig<any>, value: any) => {
    if (field.validateOnBlur) {
      const errorMessage = validateField(field.name, formValues[field.name]);
      setErrors((prevErrors) => ({
        ...prevErrors,
        [field.name]: errorMessage || '',
      }));
    }
    if (field.onBlur) field.onBlur(value);
  };

  const handleSubmit = () => {
    const formErrors: { [key: string]: string } = {};
    fields.forEach((field) => {
      if (field.showIf && !field.showIf(formValues)) return;
      const errorMessage = validateField(field.name, formValues[field.name]);
      if (errorMessage) {
        formErrors[field.name] = errorMessage;
      }
    });

    setErrors(formErrors);

    if (Object.keys(formErrors).length === 0) {
      onSubmit(formValues);
    } else {
      focusFirstInvalid(formElementRef.current);
    }
  };

  const handleValidateForm = () => {
    const formErrors: { [key: string]: string } = {};
    fields.forEach((field) => {
      if (field.showIf && !field.showIf(formValues)) return;
      const errorMessage = validateField(field.name, formValues[field.name]);
      if (errorMessage) {
        formErrors[field.name] = errorMessage;
      }
    });

    setErrors(formErrors);

    return Object.keys(formErrors).length === 0;
  }

  const handleSetFieldValue = (fieldName: string, value: any, validation = false) => {
    setFormValues((prevValues) => {
      const newValues = { ...prevValues };
      newValues[fieldName] = value;
      return newValues;
    });

    if (validation) {
      const errorMessage = validateField(fieldName, value);
      setErrors((prevErrors) => ({
        ...prevErrors,
        [fieldName]: errorMessage || '',
      }));
    }
  };

  const scrollIntoViewWithOffset = (offset = 120) => {
    if (formElementRef.current) {
      const elementPosition = formElementRef.current.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.scrollY - offset;
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  useImperativeHandle(ref, () => ({
    getValues: () => ({...formValues}),
    setFieldValue: handleSetFieldValue,
    submitForm: handleSubmit,
    validateForm: handleValidateForm,
    scrollIntoView: scrollIntoViewWithOffset
  }));

  return (
    <form
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        handleSubmit();
      }}
      ref={formElementRef}
    >
      <Flex vertical gap={configDefault.gap}>
        {fields.map((field) => {
          const shouldShow = field.showIf ? field.showIf(formValues) : true;
          const isDisabled = field.disabledIf ? field.disabledIf(formValues) : field.disabled;

          const inputProps: { [key: string]: any } = {
            label: field.label,
            value: field.formatValue ? field.formatValue(formValues) : formValues[field.name] || undefined,
            onChange: (value: any) =>
              handleFieldChange(field.name, value, field.onChange),
            onBlur: (value: any) =>
              handleFieldBlur(field, value),
            // required: field.required,
            id: field.name,
            disabled: isDisabled,
            readOnly: field.readOnly,
            error: errors[field.name],
            options: field.options,
          };

          // Dodaj componentProps ako postoji
          if (field.componentProps) {
            Object.keys(field.componentProps).forEach((key) => {
              const value = field.componentProps![key];
              inputProps[key] = formValues[key] !== undefined ? formValues[key] : value;
            });
          }

          return shouldShow ? (
            <div key={field.name}>
              {field.beforeElement}
              {createElement(field.component, inputProps)}
              {field.afterElement}
            </div>
          ) : null
        })}
      </Flex>
      {configDefault.button && <Button btnType="primary" iconPosition="right" text={configDefault.button} type="submit" style={{ width: '100%' }}>
        <ArrowRightIcon color={colors.white} />
      </Button>}
    </form>
  );
});

